import axios from 'axios';
import { storage } from '../utils';
import type { UpdateAudioRequest, GenerateFromPropertiesRequest, SaveGeneratedAudioRequest } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

const getAuthHeaders = () => {
  const token = storage.getToken();
  return {
    Authorization: `Bearer ${token}`,
  };
};

export interface AudioSearchParams {
  genre?: string;
  mood?: string;
  instruments?: string;
  status?: string;
  type?: string;
  search?: string;
  pageSize?: number;
  pageIndex?: number;
  sortBy?: string;
}

export const audioLibraryApi = {
  getFilterOptions: async (): Promise<{ genres: string[]; moods: string[]; instruments: string[] }> => {
    const response = await axios.get(`${API_BASE_URL}/api/v1/admin/audio/filter-options`, {
      headers: getAuthHeaders(),
    });
    return response.data.data;
  },

  findMany: async (params: AudioSearchParams = {}) => {
    const response = await axios.get(`${API_BASE_URL}/api/v1/admin/audio`, {
      params,
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  findById: async (id: string) => {
    const response = await axios.get(`${API_BASE_URL}/api/v1/admin/audio/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  updateAudio: async (id: string, data: UpdateAudioRequest) => {
    const response = await axios.patch(`${API_BASE_URL}/api/v1/admin/audio/${id}`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  deleteAudio: async (id: string) => {
    const response = await axios.delete(`${API_BASE_URL}/api/v1/admin/audio/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  download: async (id: string, filename: string) => {
    const response = await axios.get(`${API_BASE_URL}/api/v1/admin/audio/download/${id}`, {
      headers: getAuthHeaders(),
      responseType: 'blob',
    });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  },

  restoreAudio: async (id: string) => {
    const response = await axios.post(`${API_BASE_URL}/api/v1/admin/audio/${id}/restore`, {}, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  generateSoundEffect: async (data: { prompt: string; durationSeconds?: number; name?: string }) => {
    const response = await axios.post(`${API_BASE_URL}/api/v1/admin/audio/generate-sfx`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  generateFromProperties: async (data: GenerateFromPropertiesRequest) => {
    const response = await axios.post(`${API_BASE_URL}/api/v1/admin/audio/generate-from-properties`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  saveGeneratedAudio: async (data: SaveGeneratedAudioRequest) => {
    const response = await axios.post(`${API_BASE_URL}/api/v1/admin/audio/save-generated`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  validateFilmPlan: async (planId: string) => {
    const response = await axios.get(`${API_BASE_URL}/api/v1/admin/audio/validate-film/${planId}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  parseAudioPrompt: async (prompt: string) => {
    const response = await axios.post(`${API_BASE_URL}/api/v1/admin/audio/parse-audio-prompt`, { prompt }, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  analyzeFilmPlan: async (planId: string) => {
    const response = await axios.post(`${API_BASE_URL}/api/v1/admin/audio/analyze-film`, { planId }, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  reanalyzeSegment: async (planId: string, timeframe: string, reasoning: string) => {
    const response = await axios.post(`${API_BASE_URL}/api/v1/admin/audio/reanalyze-segment`, { planId, timeframe, reasoning }, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },
};

export default audioLibraryApi;
